import { roleAtLeast, type UserRole } from '@atlas/types'
import { useId } from 'react'
import { ConfirmDialog } from '@/components/confirm/ConfirmDialog'
import { ROLE_OPTIONS } from '@/lib/roles'
import type { UserDTO } from '@/services/user.service'

// Role-change dialog (feature-local, presentational → storiable). A role picker inside
// a confirmation dialog: deliberate confirmation, clear target + operation. A demotion
// (new role below the current one) is styled destructively. Uses the dedicated role
// endpoint via the caller — never the profile-update route.

export interface RoleEditDialogProps {
	user: UserDTO | null
	value: UserRole
	onChange: (role: UserRole) => void
	onConfirm: () => void
	onCancel: () => void
	isPending?: boolean
	/** Shown in place ALONGSIDE the status-bar report, so the retry control sits next to the reason. */
	errorMessage?: string | null
}

export function RoleEditDialog({
	user,
	value,
	onChange,
	onConfirm,
	onCancel,
	isPending,
	errorMessage,
}: RoleEditDialogProps) {
	const selectId = useId()
	if (!user) return null

	// A demotion = the selected role ranks below the user's current role.
	const isDemotion = !roleAtLeast(value, user.role as UserRole)

	return (
		<ConfirmDialog
			open={!!user}
			title="Change role"
			description={`Set the role for ${user.displayName} (${user.email}).`}
			confirmLabel="Update role"
			confirmVariant={isDemotion ? 'danger' : 'primary'}
			submitting={isPending}
			busyLabel="Working…"
			errorMessage={errorMessage}
			onConfirm={onConfirm}
			onCancel={onCancel}
		>
			<div className="flex flex-col text-sm">
				<label htmlFor={selectId}>Role</label>
				<select
					id={selectId}
					className="border rounded-ds-base px-2 py-1 h-10"
					value={value}
					onChange={(e) => onChange(e.target.value as UserRole)}
				>
					{ROLE_OPTIONS.map((o) => (
						<option key={o.value} value={o.value}>
							{o.label}
						</option>
					))}
				</select>
			</div>
		</ConfirmDialog>
	)
}
